import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ENVIRONMENT, Environment } from '@netfoundry-ui/shared/model';
import { Observable, of, Subject, throwError } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { LoggerService } from './logger.service';
import { HTTP_CLIENT } from './token.service';

/**
 * Which product extensions a network has switched on, read by network id rather than off the network the
 * console has selected. `NetworkExtensionGuard` reads `ApiService.currentNetwork`, which is what the
 * Network sub-app is pointed at, so a sub-app scoped to a frontdoor needs this instead. An error is
 * passed on rather than read as "nothing enabled", and the caller decides what to do with it.
 */
@Injectable({ providedIn: 'root' })
export class NetworkExtensionService {
    /** The network id each {@link forget} cleared, or '' for every network. A holder of an answer re-reads on it. */
    readonly forgotten$: Observable<string>;

    /** One in-flight or settled read per network. Extensions change by an operator action, not on their own. */
    private readonly byNetwork = new Map<string, Observable<string[]>>();
    private readonly forgottenSource = new Subject<string>();

    constructor(
        private logger: LoggerService,
        @Inject(HTTP_CLIENT) private http: HttpClient,
        @Inject(ENVIRONMENT) private environment: Environment
    ) {
        this.forgotten$ = this.forgottenSource.asObservable();
    }

    /**
     * The extension keys enabled on `networkId`, such as `IPSEC_TUNNELER`. Cached for the session, so the
     * surfaces asking about one network cost one request between them. A console built without the v3 API
     * answers none.
     */
    public enabledExtensions(networkId: string): Observable<string[]> {
        if (!networkId) {
            return throwError(() => new Error('enabledExtensions: no network id'));
        }
        if (!this.environment.v3Enabled) {
            return of([]);
        }
        const cached = this.byNetwork.get(networkId);
        if (cached) {
            return cached;
        }
        const url = `${this.environment.v3apiUrl}networks/${networkId}?embed=extensions`;
        const request = this.http
            .get(url, { headers: new HttpHeaders().set('Accept', 'application/hal+json') })
            .pipe(
                map((network: any) =>
                    (network?._embedded?.extensions ?? [])
                        .map((row: any) => row?.extension)
                        .filter((extension: any) => !!extension)
                ),
                shareReplay({ bufferSize: 1, refCount: false })
            );
        this.byNetwork.set(networkId, request);
        return request;
    }

    /** Whether one extension is on. See {@link enabledExtensions} for what an error means. */
    public hasExtension(networkId: string, extension: string): Observable<boolean> {
        return this.enabledExtensions(networkId).pipe(map((extensions) => extensions.includes(extension)));
    }

    /**
     * Forget what was read for `networkId`, or for every network when none is named. For an operator who has just
     * switched an extension on or off. Emits on {@link forgotten$}.
     */
    public forget(networkId = '') {
        if (networkId) {
            this.byNetwork.delete(networkId);
        } else {
            this.byNetwork.clear();
            this.logger.info('NetworkExtensionService: cleared every cached extension list');
        }
        this.forgottenSource.next(networkId);
    }
}
